import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataDisplayTable, WithSubData } from 'ui-kit/Table/DataDisplayTable';
import { ColumnDef, SortingState, RowSelectionState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { LinkButton, useDialog } from 'ui-kit';
import { RemittanceFile } from './types';
import { mockSelectedFile } from './mockRemittanceData';
import { useGetUserCompanyRemittanceTapes } from './getUserCompanyRemittanceTapes_gql';
import { useServicedDeals } from './getServicedDeals_gql';
import { FilterValue, PillFilters } from 'features/common-elements';
import { FileState } from '__generated__/globalTypes';
import { remittanceFiltersConfig } from './remittanceFilters.config';
import {
  APPROVAL_BADGE_BASE_CLASSES,
  APPROVAL_BADGE_STYLES,
  APPROVAL_LABELS_TABLE,
  type RemittanceUploadStatus,
} from './remittanceApprovalBadges';
import { IconCircleCheckFilled, IconNote } from '@tabler/icons-react';
import NotesDialog from 'features/common-elements/NotesDialog';
import MapRequiredFieldsCard from 'features/loanTape/fieldMapping/MapRequiredFieldsCard';
import { getOfferingIdFromDealId } from './utils';
import { useAddRemittanceNote, useRemittanceNotes } from './useRemittanceNotes';
import { Note } from 'features/deals/dealCards/Diligence/DealDocumentTypes';
import { ProgressIndicator } from 'ui-kit';
import type { InProgressRemittanceUpload } from './RemittanceHomePage';
import useTableQueryParams from 'hooks/useTableQueryParams';

type StatusInfo = {
  label: string;
  colorClass: string;
  textColor: string;
};

const REMITTANCE_UPLOAD_HISTORY_PATH = '/portfolio/remittance/upload-history';

/** Row shape for table: RemittanceFile plus optional upload progress for optimistic rows. */
export type RemittanceFileRow = RemittanceFile & { uploadProgress?: number };

export interface RemittanceUploadHistoryProps {
  inProgressUploads?: InProgressRemittanceUpload[];
  company?: { id: string; name: string };
  onOptimisticReplaced?: (uploadIds: string[]) => void;
}

export const RemittanceUploadHistory = ({
  inProgressUploads = [],
  company: companyProp,
  onOptimisticReplaced,
}: RemittanceUploadHistoryProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const noteDialog = useDialog();
  const openNoteDialog = useCallback(() => noteDialog.openDialog(), [noteDialog.openDialog]);
  const [selected, setSelected] = useState<RowSelectionState>({});
  const [filters, setFilters] = useState<FilterValue<typeof remittanceFiltersConfig>[]>([]);
  // Client-side pagination. The underlying `GetUserCompanyRemittanceTapes` query
  // returns the full tape list for the company (no server paging/filter yet); this
  // hook just tracks page/pageSize and the PillFilters narrow the list before slicing.
  const { params, updateParams } = useTableQueryParams<
    FilterValue<typeof remittanceFiltersConfig>,
    string
  >({ pageSize: 10 });

  const {
    data: tapesData,
    loading: tapesLoading,
    error: tapesError,
  } = useGetUserCompanyRemittanceTapes({
    pollInterval: 5000,
  });
  const { data: servicedDealsData } = useServicedDeals();

  const offeringIdToName = useMemo(() => {
    const deals = servicedDealsData?.getServicedDeals ?? [];
    return Object.fromEntries(
      deals
        .filter((d) => d.listing?.id != null && d.listing?.name != null)
        .map((d) => [d.listing!.id, d.listing!.name] as [string, string]),
    );
  }, [servicedDealsData]);

  const dealIdToName = useMemo(() => {
    const deals = servicedDealsData?.getServicedDeals ?? [];
    return Object.fromEntries(
      deals.map((d) => [d.id, d.listing?.name ?? d.id] as [string, string]),
    );
  }, [servicedDealsData]);

  const apiTapesAsFiles: RemittanceFile[] = useMemo(() => {
    const company = tapesData?.user?.company;
    if (!company?.remittance_tapes) return [];
    const tapes = company.remittance_tapes;
    return tapes.map((t): RemittanceFile => {
      const createdAt =
        typeof t.created_at === 'string' ? t.created_at : new Date(t.created_at).toISOString();
      return {
        id: t.id,
        createdDate: createdAt,
        deal_id: t.deal_id,
        deal_name: offeringIdToName[t.offering_id],
        payment_month: t.payment_month ?? undefined,
        file_name: t.file_name ?? undefined,
        upload_id: t.id,
        company: { id: company.id, name: company.name },
        file_state: t.file_state,
        upload_status: t.upload_status ?? null,
        last_updated_date: createdAt,
        __typename: 'RemittanceFile',
      };
    });
  }, [tapesData, offeringIdToName]);

  const apiTapeIds = useMemo(() => new Set(apiTapesAsFiles.map((f) => f.id)), [apiTapesAsFiles]);

  useEffect(() => {
    const replaced = inProgressUploads
      .filter((u) => apiTapeIds.has(u.uploadId))
      .map((u) => u.uploadId);
    if (replaced.length > 0) onOptimisticReplaced?.(replaced);
  }, [apiTapeIds, inProgressUploads, onOptimisticReplaced]);

  const allRemittanceFiles: RemittanceFileRow[] = useMemo(() => {
    const company = companyProp ?? tapesData?.user?.company;
    const optimisticRows: RemittanceFileRow[] = inProgressUploads
      .filter((u) => !apiTapeIds.has(u.uploadId))
      .map((u) => ({
        id: u.uploadId,
        createdDate: new Date().toISOString(),
        deal_id: u.dealId,
        deal_name: dealIdToName[u.dealId] ?? '—',
        payment_month: u.paymentMonth,
        file_name: u.fileName,
        upload_id: u.uploadId,
        company: company ? { id: company.id, name: company.name } : { id: '', name: '' },
        file_state: FileState.NEW,
        upload_status: null,
        last_updated_date: new Date().toISOString(),
        __typename: 'RemittanceFile' as const,
        uploadProgress: u.progress,
      }));

    const apiSorted = [...apiTapesAsFiles].sort(
      (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    );
    return [...optimisticRows, ...apiSorted];
  }, [
    apiTapeIds,
    apiTapesAsFiles,
    companyProp,
    tapesData?.user?.company,
    dealIdToName,
    inProgressUploads,
  ]);

  // Apply active filters client-side. No server-side filter support today; the full
  // company tape list fits comfortably in memory. deal_id / company_name are
  // substring-match ANY (ListInputFilter values) — institution-name pill accepts
  // multiple values and matches if any substring hits. The current_balance_cents
  // range filter is defined in remittanceFiltersConfig for schema consistency but
  // the row type has no balance, so it's silently ignored (falls through below).
  const filteredRemittanceFiles: RemittanceFileRow[] = useMemo(() => {
    if (filters.length === 0) return allRemittanceFiles;
    const matchesAny = (haystack: string, queries: string[]) =>
      queries.length === 0 ||
      queries.some((q) => q.length > 0 && haystack.toLowerCase().includes(q.toLowerCase()));
    return allRemittanceFiles.filter((row) => {
      for (const filter of filters) {
        if (!('values' in filter)) continue;
        if (filter.name === 'deal_id' && !matchesAny(row.deal_id ?? '', filter.values))
          return false;
        if (filter.name === 'company_name' && !matchesAny(row.company?.name ?? '', filter.values))
          return false;
      }
      return true;
    });
  }, [allRemittanceFiles, filters]);

  // Apply active sort to the full filtered set BEFORE slicing into pages. Passing a
  // pre-sliced page to DataDisplayTable with `isManual: false` would leave the
  // internal TanStack sort reordering only the ~10 visible rows; a global column
  // click should sort everything and repaginate.
  const sortedRemittanceFiles: RemittanceFileRow[] = useMemo(() => {
    if (sorting.length === 0) return filteredRemittanceFiles;
    const [s] = sorting;
    const dir = s.desc ? -1 : 1;
    const field = s.id as keyof RemittanceFileRow;
    return [...filteredRemittanceFiles].sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filteredRemittanceFiles, sorting]);

  // Clamp the requested page number to the filtered result set's page count. Avoids
  // landing on an empty page after a filter narrows the list below the current offset,
  // without needing a useEffect that writes back to params (which makes the
  // exhaustive-deps rule unhappy in CRA).
  const effectivePageNumber = useMemo(() => {
    const maxPage = Math.max(1, Math.ceil(sortedRemittanceFiles.length / params.pageSize));
    return Math.min(params.pageNumber, maxPage);
  }, [sortedRemittanceFiles.length, params.pageNumber, params.pageSize]);

  const pagedRemittanceFiles: RemittanceFileRow[] = useMemo(() => {
    const start = (effectivePageNumber - 1) * params.pageSize;
    return sortedRemittanceFiles.slice(start, start + params.pageSize);
  }, [sortedRemittanceFiles, effectivePageNumber, params.pageSize]);

  // Reset to page 1 when filters or sorting change — clamping via useMemo only
  // prevents landing past the last page; a user on page 3 applying a filter that
  // still has ≥3 pages would stay on page 3 of the narrowed set instead of page 1.
  // Matches the sibling RemittanceTapeDealTable pattern (uploadId change → reset).
  // `updateParams` isn't memoized, but omitting it from deps doesn't trip CRA's
  // bundled eslint (no react-hooks plugin loaded; see feedback_cra_eslint_unknown_rule).
  useEffect(() => {
    updateParams({ pageNumber: 1 });
  }, [filters, sorting]);

  // Clear selection when the user intentionally changes what the page shows —
  // filter change, sort change, or step to a new page. Watch the RAW state values,
  // not derived arrays: `filteredRemittanceFiles` gets a fresh reference every
  // 5s as the tapes query polls, which would wipe the user's selection each tick.
  // setSelected is React's stable dispatch, so it doesn't go in deps.
  useEffect(() => {
    setSelected({});
  }, [filters, sorting, params.pageNumber, params.pageSize]);

  // Get selected deal ID and the selected file. Index the PAGE (not the full list)
  // because DataDisplayTable's row keys are 0..pageSize-1 within the rendered slice.
  const selectedDealId = useMemo(() => {
    const selectedKey = Object.keys(selected).find((key) => selected[key]);
    if (!selectedKey) return null;
    const fileIndex = parseInt(selectedKey, 10);
    return pagedRemittanceFiles[fileIndex]?.deal_id || null;
  }, [selected, pagedRemittanceFiles]);

  const selectedFile = useMemo(() => {
    if (!selectedDealId) return null;
    return pagedRemittanceFiles.find((file) => file.deal_id === selectedDealId) || null;
  }, [selectedDealId, pagedRemittanceFiles]);

  // TODO: Use the actual remittance account ID when available.
  const selectedAccountId = useMemo(() => selectedFile?.id || null, [selectedFile]);
  const selectedOfferingId = useMemo(
    () => getOfferingIdFromDealId(selectedFile?.deal_id) ?? null,
    [selectedFile?.deal_id],
  );

  const { notes, loading } = useRemittanceNotes({
    offeringID: selectedOfferingId,
    accountID: selectedAccountId,
    resetKey: noteDialog.isOpen ? selectedAccountId || undefined : undefined,
    enabled: noteDialog.isOpen && Boolean(selectedAccountId) && Boolean(selectedOfferingId),
  });
  const { addNote } = useAddRemittanceNote({
    offeringID: selectedOfferingId,
    accountID: selectedAccountId,
  });

  const [notesByAccountId, setNotesByAccountId] = useState<Record<string, Note[]>>({});

  useEffect(() => {
    if (!selectedAccountId) return;
    setNotesByAccountId((prev) => ({
      ...prev,
      [selectedAccountId]: notes,
    }));
  }, [notes, selectedAccountId]);

  /** Labels for file pipeline state: have we processed the file and drafted remittance records? */
  const fileStateConfig: Record<string, StatusInfo> = {
    [FileState.NEW]: { label: 'New', colorClass: 'bg-gray-500', textColor: 'text-white' },
    [FileState.READING_FILE_HEADERS]: {
      label: 'Reading headers',
      colorClass: 'bg-gray-500',
      textColor: 'text-white',
    },
    [FileState.NEEDS_MAPPING]: {
      label: 'Needs mapping',
      colorClass: 'bg-blue-400',
      textColor: 'text-white',
    },
    [FileState.READY_TO_PROCESS]: {
      label: 'Ready to process',
      colorClass: 'bg-gray-500',
      textColor: 'text-white',
    },
    [FileState.PROCESSING]: {
      label: 'Processing',
      colorClass: 'bg-amber-500/80',
      textColor: 'text-gray-950',
    },
    [FileState.PROCESSED]: {
      label: 'Processed',
      colorClass: 'bg-gray-500',
      textColor: 'text-white',
    },
    [FileState.READY_TO_PERSIST]: {
      label: 'Ready to persist',
      colorClass: 'bg-gray-500',
      textColor: 'text-white',
    },
    [FileState.PERSISTING]: {
      label: 'Persisting',
      colorClass: 'bg-amber-500/80',
      textColor: 'text-gray-950',
    },
    [FileState.PERSISTED]: { label: 'Ready', colorClass: 'bg-green-600', textColor: 'text-white' },
    [FileState.ERROR]: { label: 'Error', colorClass: 'bg-red-400', textColor: 'text-white' },
    [FileState.ERROR_HEADER]: {
      label: 'Error (header)',
      colorClass: 'bg-red-400',
      textColor: 'text-white',
    },
    [FileState.ERROR_PASSWORD]: {
      label: 'Error (password)',
      colorClass: 'bg-red-400',
      textColor: 'text-white',
    },
  };

  /** Badge config for Approval column; uses shared remittance approval badge styles. */
  const approvalBadgeConfig: Record<string, StatusInfo> = Object.fromEntries(
    (['PENDING', 'APPROVED', 'REJECTED'] as RemittanceUploadStatus[]).map((status) => [
      status,
      {
        label: APPROVAL_LABELS_TABLE[status],
        colorClass: APPROVAL_BADGE_STYLES[status].colorClass,
        textColor: APPROVAL_BADGE_STYLES[status].textColor,
      },
    ]),
  );

  const handleAddNote = (noteText: string) => {
    addNote(noteText);
  };

  useEffect(() => {
    if (!selectedDealId) return;
    const path = location.pathname.endsWith(REMITTANCE_UPLOAD_HISTORY_PATH)
      ? REMITTANCE_UPLOAD_HISTORY_PATH
      : location.pathname;
    const params = new URLSearchParams(location.search);
    params.set('selectedDealId', selectedDealId);
    const url = `${path}?${params.toString()}`;
    window.history.replaceState(null, '', url);
  }, [selectedDealId, location.pathname, location.search]);

  const columns: ColumnDef<WithSubData<RemittanceFileRow>, any>[] = useMemo(
    () => [
      {
        id: 'createdDate',
        accessorKey: 'createdDate',
        header: 'Date Uploaded',
        cell: (cellContext) => {
          const value = cellContext.getValue() as string;
          if (!value) return '-';
          const dt = DateTime.fromISO(value).toLocal();
          return dt.toFormat('MMM d, yyyy, h:mm a');
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: true,
      },
      {
        id: 'deal_name',
        accessorFn: (row) => row.deal_name ?? '—',
        header: 'Deal Name',
        cell: (cellContext) => <span>{cellContext.getValue() as string}</span>,
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: true,
      },
      {
        id: 'payment_month',
        accessorKey: 'payment_month',
        header: 'Payment month',
        cell: (cellContext) => {
          const value = cellContext.getValue() as string | undefined;
          return <span>{value ?? '—'}</span>;
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: true,
      },
      {
        id: 'file_name',
        accessorKey: 'file_name',
        header: 'File name',
        cell: (cellContext) => {
          const value = cellContext.getValue() as string | undefined;
          return (
            <span className="block max-w-[200px] truncate" title={value ?? ''}>
              {value ?? '—'}
            </span>
          );
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: true,
      },
      {
        id: 'company',
        accessorKey: 'company',
        header: 'Institution Name',
        cell: (cellContext) => {
          const company = cellContext.getValue() as RemittanceFile['company'];
          return company?.name || '-';
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: false,
      },
      {
        id: 'notes',
        header: 'Notes',
        cell: (cellContext) => {
          const file = cellContext.row.original;
          const accountId = file.id;
          const accountNotes = notesByAccountId[accountId] || [];
          const hasNotes = accountNotes.length > 0;
          return (
            <button
              type="button"
              aria-label="Open notes"
              className={hasNotes ? 'text-gray-600 hover:text-gray-400' : 'text-gray-500'}
              onClick={() => {
                setSelected({ [cellContext.row.index]: true });
                openNoteDialog();
              }}
            >
              <IconNote size={20} stroke={1.5} />
            </button>
          );
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: false,
      },
      {
        id: 'file_state',
        header: 'File status',
        cell: (cellContext) => {
          const row = cellContext.row.original as RemittanceFileRow;
          const progress = row.uploadProgress;
          if (progress !== undefined) {
            if (progress >= 100) {
              return (
                <div className="flex w-[140px] items-center gap-2" aria-label="Upload complete">
                  <IconCircleCheckFilled size={22} className="text-green-400" aria-hidden />
                  <span className="text-xs text-green-400">Uploaded</span>
                </div>
              );
            }
            return (
              <div className="flex w-[140px] flex-col gap-1">
                <span className="text-xs text-gray-400">Uploading</span>
                <ProgressIndicator percent={progress} />
              </div>
            );
          }
          const fileState = row.file_state;
          const config = fileStateConfig[fileState] ?? {
            label: fileState,
            colorClass: 'bg-gray-500',
            textColor: 'text-white',
          };
          return (
            <span
              className={`rounded px-2 py-0.5 text-sm ${config.colorClass} ${config.textColor}`}
              aria-label={config.label}
            >
              {config.label}
            </span>
          );
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: false,
      },
      {
        id: 'upload_status',
        header: 'Approval',
        cell: (cellContext) => {
          const uploadStatus = cellContext.row.original.upload_status;
          if (uploadStatus == null) return <span className="text-gray-500">—</span>;
          const badge = approvalBadgeConfig[uploadStatus];
          if (!badge) return <span>{uploadStatus}</span>;
          return (
            <span
              className={`${APPROVAL_BADGE_BASE_CLASSES} ${badge.colorClass} ${badge.textColor}`}
              aria-label={badge.label}
            >
              {badge.label}
            </span>
          );
        },
        meta: {
          alignment: 'left',
          numeric: false,
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        cell: (cellContext) => {
          const dealId = cellContext.row.original.deal_id;
          const uploadId = cellContext.row.original.upload_id;
          const hasUpload = Boolean(uploadId);
          return (
            <LinkButton
              text="View Loans"
              disabled={!hasUpload}
              onClick={() =>
                hasUpload
                  ? navigate(
                      `/portfolio/remittance/dealId/${encodeURIComponent(dealId)}/uploadId/${uploadId}`,
                    )
                  : undefined
              }
            />
          );
        },
        meta: {
          headerNoPill: true,
        },
        enableSorting: false,
      },
    ],
    [navigate, notesByAccountId, openNoteDialog],
  );

  if (tapesLoading) {
    return <div className="p-6 text-gray-400">Loading remittance tapes…</div>;
  }

  if (tapesError) {
    return (
      <div className="rounded border border-red-500/50 bg-background-canvas p-6 text-red-400">
        Failed to load remittance tapes: {tapesError.message}
      </div>
    );
  }

  return (
    <>
      <div style={{ height: 'fit-content' }}>
        <div className="mb-3 flex items-center justify-between">
          <PillFilters
            filtersConfig={remittanceFiltersConfig}
            currentFilters={filters}
            setCurrentFilters={setFilters}
          />
          <div className="flex gap-2">
            {/*
            Toolbar actions (uncomment when ready; add Button, IconButton from ui-kit and IconDownload from @tabler/icons-react; const hasSelectedRows = Boolean(selectedDealId)):
            <Button label="Remove" type="secondary" size="medium" disabled={!hasSelectedRows} onClick={() => console.log('Remove clicked for deal ID:', selectedDealId)}>Remove</Button>
            <IconButton disabled={!hasSelectedRows} onClick={() => console.log('Download for deal ID:', selectedDealId)} icon={<IconDownload stroke={2} />} />
            */}
          </div>
        </div>
        <DataDisplayTable
          data={pagedRemittanceFiles}
          // @ts-expect-error - Type mismatch between our column type and DataDisplayTable generic expectations
          columns={columns}
          noDataMessage={
            filters.length > 0
              ? 'No remittance tapes match the current filters'
              : 'No remittance tapes uploaded yet'
          }
          sorting={{
            state: sorting,
            onSortingChanged: setSorting,
            // Sort is applied manually above (sortedRemittanceFiles useMemo) before
            // slicing into pages. Leaving isManual: false would let TanStack re-sort
            // the visible page with a different comparator (alphanumeric vs our
            // localeCompare), reordering rows on the page inconsistently with the
            // cross-page ordering we already computed.
            isManual: true,
          }}
          selection={{
            selected,
            onSelectionChange: setSelected,
            enableMultiRowSelection: false,
          }}
          isSelectionInfoIcon={false}
          pagination={{
            updateParams,
            queryParams: { ...params, pageNumber: effectivePageNumber },
            totalItems: filteredRemittanceFiles.length,
          }}
        />
        {selectedFile && (
          <NotesDialog
            dialog={noteDialog}
            documentName={`${selectedFile.company?.name ?? 'Deal'} - ${selectedFile.deal_id}`}
            notes={notes}
            handleAddNote={handleAddNote}
          />
        )}
      </div>
      <div className="relative">
        {selectedDealId &&
          selectedFile?.file_state === FileState.NEEDS_MAPPING &&
          mockSelectedFile?.headers && <MapRequiredFieldsCard selectedFile={mockSelectedFile} />}
      </div>
    </>
  );
};
